import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ShoppingBag, AlertCircle, ArrowRight, Loader2 } from 'lucide-react'
import { kassaAPI } from '@/lib/api'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

const formatEuro = (cents) => `€${(cents / 100).toFixed(2)}`

export default function KassaAccountCard() {
  const [account, setAccount] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let alive = true
    kassaAPI.getMyAccount()
      .then(res => { if (alive) setAccount(res.data) })
      .catch(err => { if (alive) setError(err.response?.data?.error || err.message) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  if (loading) {
    return (
      <Card className="border-neon-blue/30">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-neon-blue" />
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className="border-destructive/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-destructive" />
            <span>Openstaande rekening</span>
          </CardTitle>
          <CardDescription>Kassa niet bereikbaar: {error}</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  const linked = account?.linked
  const openTotal = (account?.accounts || []).reduce(
    (sum, a) => sum + (a.outstanding_cents ?? a.open_cents ?? a.balance_cents ?? a.total_cents ?? 0), 0
  )
  const openCount = account?.accounts?.length || 0

  return (
    <Card className="border-neon-blue/30">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShoppingBag className="h-5 w-5 text-neon-blue" />
          <span>Openstaande rekening</span>
        </CardTitle>
        <CardDescription>Jouw openstaand saldo op de bar</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!linked ? (
          <div className="flex items-start gap-2 text-sm text-muted-foreground p-3 rounded-lg border border-border/50 bg-card/50">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Je LAN-account is nog niet gekoppeld aan een bar-kaart. Vraag een admin om je te koppelen via <em>Admin → Kaarten</em>.</span>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-center p-6 rounded-lg border border-neon-blue/20 bg-neon-blue/5">
              <div className="text-center">
                <p className="text-5xl font-bold text-neon-blue">{formatEuro(openTotal)}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {openCount === 0 ? 'niets openstaand' : `${openCount} open rekening${openCount !== 1 ? 'en' : ''}`}
                </p>
              </div>
            </div>
            {openCount > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground font-medium">Openstaande orders</p>
                {(account.accounts || []).map((a) => (
                  <div key={a.order_id} className="flex items-center justify-between p-2.5 rounded-lg border border-border/50 bg-card/50">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm">Order #{a.order_id}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {a.event?.name} · {new Date(a.created_at).toLocaleString('nl-BE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <span className="text-sm font-semibold text-neon-blue ml-3 flex-shrink-0">
                      {formatEuro(a.outstanding_cents ?? a.total_cents ?? 0)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        <Button asChild variant="outline" className="w-full">
          <Link to="/bestellen">
            <ShoppingBag className="h-4 w-4 mr-2" /> Naar Bestellen <ArrowRight className="ml-auto h-4 w-4" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  )
}
